import React, { useEffect, useRef, useState } from "react";
import { FacebookShareButton, WhatsappShareButton, FacebookIcon, WhatsappIcon } from "react-share";
import { useTranslation } from "next-i18next";
import exitModalIcon from "public/images/exit_modal.svg";
import Image from "next/image";
import CloseModalButton from "./closeModalButton";

type ShareStoreModalProps = {
  closeShareModal: () => void;
  storeSlug: string;
  categorySlug: string;
};

function ShareStoreModal({ closeShareModal, storeSlug, categorySlug }: ShareStoreModalProps) {
  const { t: translate } = useTranslation("offers");
  const [pathISCopied, setPathIsCopeid] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL || "https://www.yawakit.com/";

  // current orgin is used to get the current domain of the website (local, staging, production)

  const currentOrigin =
    typeof window !== "undefined" && window?.location?.origin ? window?.location?.origin : BASE_URL;

  const shareUrl = `${currentOrigin}/categories/${categorySlug}/${storeSlug}`;

  useEffect(() => {
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = "scroll";
    };
  }, []);

  function handleOutsideClick(event: React.MouseEvent<HTMLDivElement>) {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      closeShareModal();
    }
  }

  function copyPathToClipBoard() {
    navigator.clipboard.writeText(shareUrl);
    setPathIsCopeid(true);
    setTimeout(() => {
      setPathIsCopeid(false);
    }, 1000);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-10 bg-black/50 backdrop-blur-sm"
      onClick={handleOutsideClick}
    >
      <div
        ref={modalRef}
        className="relative bg-white p-8 pt-4 px-6 rounded-xl flex flex-col gap-5 items-center shadow-lg max-w-lg w-full"
      >
        <CloseModalButton handleClick={closeShareModal} className="self-end" />
        <h3 className="text-xl font-semibold">{translate("share_store_with_friends")}</h3>
        <p className="text-gray-500">{translate("share_content")}</p>
        <div className="flex flex-row gap-5">
          {/* Facebook Share */}
          <FacebookShareButton url={shareUrl}>
            <FacebookIcon size={50} className="rounded-xl" />
          </FacebookShareButton>

          {/* WhatsApp Share */}
          <WhatsappShareButton url={shareUrl}>
            <WhatsappIcon size={50} className="rounded-xl" />
          </WhatsappShareButton>
        </div>
        <div className="border-2 border-gray-500 bg-slate-100 flex gap-5 items-center justify-between rounded-md w-full">
          <input
            type="text"
            className="outline-none flex-1 p-2 bg-transparent w-full"
            value={shareUrl}
            readOnly
          />
          <button onClick={copyPathToClipBoard} className="bg-white text-blue rounded-md p-3">
            {translate(pathISCopied ? "copied" : "copy")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShareStoreModal;
